import { createRouter, createWebHistory } from "vue-router";
import { useNProgress } from "@vueuse/integrations/useNProgress";
import { authGuard } from "@/shared/services/router/authGuard";
import { usePermission } from "@/shared/composables/usePermission";
import { storeToRefs } from "pinia";
import { useAuth } from "@/shared/stores/auth";
import { useNotify } from "@/shared/composables/useNotify";
import Login from "@/app/security/components/Login.vue";
import HealthLayout from "@/shared/components/layout/HealthLayout.vue";
import HealthPageNotFound from "@/shared/components/utils/HealthPageNotFound.vue";

const { start, done } = useNProgress();

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/auth/login",
      name: "Login",
      component: Login,
      meta: {
        requiresAuth: false,
      },
    },
    {
      path: "/",
      name: "HealthLayout",
      component: HealthLayout,
      children: [
        {
          path: "/",
          name: "Dashboard",
          component: () => import("@/app/dashboard/Dashboard.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "ADMINISTRADOR", "COORDENADOR"],
          },
        },
        {
          path: "/exams",
          name: "Exams",
          component: () => import("@/app/study/views/Studies.vue"),
          meta: {
            requiresAuth: true,
            permissions: [
              "SUPER_ADMINISTRADOR",
              "ADMINISTRADOR",
              "COORDENADOR",
              "MEDICO",
              "MEDICO_LAUDADOR",
              "DIGITADOR",
            ],
          },
        },
        {
          path: "/exams/:id?/report",
          name: "Report",
          component: () => import("@/app/study/modules/report/views/Report.vue"),
          meta: {
            requiresAuth: true,
            permissions: [
              "SUPER_ADMINISTRADOR",
              "ADMINISTRADOR",
              "COORDENADOR",
              "MEDICO",
              "MEDICO_LAUDADOR",
              "DIGITADOR",
            ],
          },
        },
        {
          path: "/profiles",
          name: "Profiles",
          component: () => import("@/app/profiles/views/Profiles.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/medical-specialties",
          name: "MedicalSpecialties",
          component: () => import("@/app/medical_specialties/views/MedicalSpecialties.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "ADMINISTRADOR"],
          },
        },
        {
          path: "/clinical-protocols",
          name: "ClinicalProtocols",
          component: () => import("@/app/clinical_protocol/views/ClinicalProtocols.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "ADMINISTRADOR", "COORDENADOR"],
          },
        },
        {
          path: "/acquisition-modalities",
          name: "AcquisitionModalities",
          component: () => import("@/app/acquisition_modalities/views/AcquisitionModalities.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/users",
          name: "Users",
          component: () => import("@/app/users/views/Users.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "ADMINISTRADOR"],
          },
        },
        {
          path: "/logs",
          name: "Logs",
          component: () => import("@/app/logs/views/Logs.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/user/register",
          name: "RegisterUser",
          component: () => import("@/app/users/views/RegisterUser.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "ADMINISTRADOR"],
          },
        },
        {
          path: "/user/:id?",
          name: "User",
          props: true,
          component: () => import("@/app/users/views/User.vue"),
          meta: {
            requiresAuth: false,
          },
        },
        {
          path: "/institutions",
          name: "Institutions",
          component: () => import("@/app/institution/views/institutions.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/institution/:id?",
          name: "Institution",
          props: true,
          component: () => import("@/app/institution/views/Institution.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/institution/register",
          name: "RegisterInstitution",
          component: () => import("@/app/institution/views/RegisterInstitution.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/manufacturers",
          name: "Manufacturers",
          component: () =>
            import("@/app/institution/modules/device/modules/manufacturer/views/Manufacturers.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/shortcut-phrases",
          name: "ShortcutPhrases",
          component: () => import("@/app/users/modules/shortcut_phrase/views/ShortcutPhrases.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "MEDICO_LAUDADOR"],
          },
        },
        {
          path: "/report-masks",
          name: "ReportMasks",
          component: () => import("@/app/users/modules/report_mask/views/ReportMasks.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "MEDICO_LAUDADOR"],
          },
        },
        {
          path: "/report-mask/:id?",
          name: "ReportMask",
          component: () => import("@/app/users/modules/report_mask/components/ReportMask.vue"),
          props: true,
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR", "MEDICO_LAUDADOR"],
          },
        },
        {
          path: "/locations",
          name: "locations",
          component: () =>
            import("@/app/institution/modules/device/modules/location/views/locations.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/companies",
          name: "Companies",
          component: () => import("@/app/company/views/Companies.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/roles",
          name: "Roles",
          component: () => import("@/app/role/views/Roles.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/sectors",
          name: "Sectors",
          component: () => import("@/app/sector/views/Sectors.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/requesting-doctors",
          name: "RequestingDoctors",
          component: () => import("@/app/requesting_doctor/views/RequestingDoctors.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/exam-types",
          name: "ExamTypes",
          component: () => import("@/app/exam_type/views/ExamTypes.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/body-parts",
          name: "BodyParts",
          component: () => import("@/app/body_part/views/BodyParts.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/procedures",
          name: "Procedures",
          component: () => import("@/app/procedure/views/Procedures.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
        {
          path: "/exam/register",
          name: "RegisterExam",
          component: () => import("@/app/exam/views/RegisterExam.vue"),
          meta: {
            requiresAuth: true,
            permissions: ["SUPER_ADMINISTRADOR"],
          },
        },
      ],
    },
    {
      path: "/:pathMatch(.*)*",
      name: "HealthPageNotFound",
      component: HealthPageNotFound,
    },
  ],
});

router.beforeEach((to, from, next) => {
  start();

  const { user } = storeToRefs(useAuth());

  if (to.name === "User") {
    if (!user) {
      return next({
        query: { redirect: to.fullPath },
      });
    }

    const hasPermission = usePermission("SUPER_ADMINISTRADOR", "ADMINISTRADOR");

    const routeUserId = to.params.id;
    const authUserId = user.value.id;

    if (hasPermission) {
      return next();
    }

    if (String(routeUserId) === String(authUserId)) {
      return next();
    }

    useNotify({
      message: "Acesso negado: o usuário não possui as permissões necessárias.",
      type: "warning",
    });
    return next({ name: "Exams" });
  }

  authGuard(to, from, next);
});

router.afterEach(() => {
  done();
});

export default router;
